import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import {
  AutoFixHigh as NormalizeIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

interface NormalizedAddress {
  originalAddress: string;
  city: string;
  district: string;
  commune: string;
  newCity?: string;
  newCommune?: string;
}

interface TrialAddressNormalizerProps {
  onNormalize: (address: string) => Promise<NormalizedAddress>;
  remainingAttempts: number;
}

const TrialAddressNormalizer: React.FC<TrialAddressNormalizerProps> = ({
  onNormalize,
  remainingAttempts
}) => {
  const [address, setAddress] = useState('');
  const [normalizedAddress, setNormalizedAddress] = useState<NormalizedAddress | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleNormalize = async () => {
    if (!address.trim()) {
      setError('Vui lòng nhập địa chỉ cần chuẩn hóa');
      return;
    }

    if (address.length > 255) {
      setError('Địa chỉ không được vượt quá 255 ký tự');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await onNormalize(address);
      setNormalizedAddress(result);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra khi chuẩn hóa địa chỉ');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(event.target.value);
    if (error) setError(null);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 600, width: '100%' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ textAlign: 'center' }}>
          <NormalizeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" color="primary" fontWeight={600} gutterBottom>
            Dùng thử chuẩn hóa địa chỉ
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nhập địa chỉ để trải nghiệm tính năng chuẩn hóa tự động
          </Typography>
        </Box>

        {/* Remaining attempts */}
        <Box sx={{ textAlign: 'center' }}>
          <Chip
            label={`Còn lại ${remainingAttempts}/5 lượt dùng thử hôm nay, hãy đăng nhập để tiếp tục sử dụng.`}
            color={remainingAttempts > 0 ? 'primary' : 'error'}
            variant="outlined"
          />
        </Box>

        <Divider />

        {/* Input section */}
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Nhập địa chỉ cần chuẩn hóa"
            value={address}
            onChange={handleAddressChange}
            multiline
            rows={3}
            placeholder="Ví dụ: 123 đường Nguyễn Huệ, quận 1, TP.HCM"
            inputProps={{
              maxLength: 255
            }}
            helperText={`${address.length}/255 ký tự`}
            disabled={loading || remainingAttempts <= 0}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleNormalize}
            disabled={!address.trim() || loading || remainingAttempts <= 0}
            startIcon={loading ? <CircularProgress size={20} /> : <NormalizeIcon />}
            sx={{ py: 1.5 }}
          >
            {loading ? 'Đang chuẩn hóa...' : 'Chuẩn hóa địa chỉ'}
          </Button>
        </Stack>

        {/* Error message */}
        {error && (
          <Alert severity="error" icon={<ErrorIcon />}>
            {error}
          </Alert>
        )}

        {/* Success message */}
        {success && (
          <Alert severity="success" icon={<CheckIcon />}>
            Chuẩn hóa địa chỉ thành công!
          </Alert>
        )}

        {/* Results */}
        {normalizedAddress && (
          <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Kết quả chuẩn hóa
            </Typography>
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Địa chỉ gốc:
                </Typography>
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                  {normalizedAddress.originalAddress}
                </Typography>
              </Box>

              <Divider />

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Tỉnh/Thành phố:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {normalizedAddress.city || 'Không xác định'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Quận/Huyện:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {normalizedAddress.district || 'Không xác định'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Phường/Xã:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {normalizedAddress.commune || 'Không xác định'}
                  </Typography>
                </Box>
              </Stack>

              {(normalizedAddress.newCity || normalizedAddress.newCommune) && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" color="primary" fontWeight={600} gutterBottom>
                    Thông tin địa chỉ mới:
                  </Typography>
                  <Stack spacing={2}>
                    {normalizedAddress.newCity && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon color="info" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Tỉnh/Thành phố mới:
                        </Typography>
                        <Typography variant="body1" fontWeight={500} color="info.main">
                          {normalizedAddress.newCity}
                        </Typography>
                      </Box>
                    )}
                    {normalizedAddress.newCommune && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon color="info" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Phường/Xã mới:
                        </Typography>
                        <Typography variant="body1" fontWeight={500} color="info.main">
                          {normalizedAddress.newCommune}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    </Paper>
  );
};

export default TrialAddressNormalizer; 